import React, { useMemo } from 'react';
import { formatCategoryLabel, byCategoryOrder } from './api';
import { t } from './i18n';

const COLORS = {
  primary: '#1a4d8f',
  text: '#1f2937',
  muted: '#6b7280',
  border: '#e5e7eb',
  bg: '#f7f8fa',
};

// "Jump to category" dropdown listing EVERY category that exists in the current
// location scope — even ones whose places haven't been paged in yet — with its
// full count. Picking one jumps to that category's section (loading more
// results first if needed).
//
// `categories` is the location-scoped list ({type, count}) the list page
// already loads; we fall back to deriving from `results` if it isn't passed.
// Anchor IDs are `cat-<type>` on the category dividers in the grid.
export default function CategoryToc({
  categories, results, sortMode, onJump, variant = 'dropdown',
  selectedTypes, onToggleType, onClearTypes,
}) {
  // Jumping needs category groupings, so it only applies to the category
  // sort. TICKING DOES NOT: narrowing to synagogues is just as reasonable
  // when the list is sorted by distance, and gating the whole block on the
  // sort would make the filter vanish the moment someone taps "Closest to
  // me". So the rail builds its list either way and only the jump is gated.
  const isCategorySort = !sortMode || sortMode === 'category';
  const canFilter = variant === 'list' && typeof onToggleType === 'function';

  // Which categories are actually rendered on the page right now. With a
  // type filter on, the rail lists every category in the location (so you
  // can switch to one) while the page holds only the selected one — so the
  // count must not offer to jump to a section that is not there.
  const present = useMemo(() => new Set(
    (Array.isArray(results) ? results : []).map((r) => r.type).filter(Boolean)
  ), [results]);

  const groups = useMemo(() => {
    if (!isCategorySort && !canFilter) return [];
    // Prefer the full location-scoped categories (so the menu lists them all,
    // independent of pagination); fall back to whatever's in results.
    if (Array.isArray(categories) && categories.length > 0) {
      return categories
        .filter((c) => c && c.type && Number(c.count) > 0)
        .map((c) => ({ type: c.type, count: Number(c.count) }))
        .sort(byCategoryOrder);
    }
    if (!Array.isArray(results) || results.length === 0) return [];
    const counts = new Map();
    const order = [];
    for (const r of results) {
      if (!r.type) continue;
      if (!counts.has(r.type)) order.push(r.type);
      counts.set(r.type, (counts.get(r.type) || 0) + 1);
    }
    return order.map((t) => ({ type: t, count: counts.get(t) }));
  }, [categories, results, isCategorySort, canFilter]);

  if (groups.length < 2) return null;
  if (!isCategorySort && !canFilter) return null;

  const jump = (type) => {
    if (!type) return;
    if (onJump) { onJump(type); return; }
    const el = document.getElementById(`cat-${type}`);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  // Sidebar variant — the list-page left rail. Each row carries a tick-box
  // that NARROWS the list to the ticked categories, and a label that jumps to
  // that category's section. Two jobs in one row, because they are the same
  // question asked twice: "I only care about synagogues."
  //
  // Ticking nothing means everything, which is why there is no "all" box —
  // an unticked list already says it. The counts stay put as boxes are
  // ticked, because /api/categories deliberately ignores the type filter:
  // a facet that re-counts itself would drop to one row the moment you
  // picked one, and there would be no way to add a second.
  if (variant === 'list') {
    const picked = new Set(selectedTypes || []);
    const any = picked.size > 0;
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            {canFilter ? t('Categories') : t('Jump to')}
          </div>
          {any && onClearTypes && (
            <button
              type="button"
              onClick={onClearTypes}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 11, fontWeight: 600, color: COLORS.primary, fontFamily: 'inherit' }}
            >
              {t('Show all')}
            </button>
          )}
        </div>
        <nav aria-label={canFilter ? t('Filter by category') : t('Jump to a category on this page')} style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {groups.map((g) => {
            const on = picked.has(g.type);
            const label = (
              <>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: on ? 700 : 400 }}>{formatCategoryLabel(g.type)}</span>
                <span style={{ color: COLORS.muted, fontSize: 12, fontWeight: 600, flexShrink: 0 }}>{g.count}</span>
              </>
            );
            if (!canFilter) {
              return (
                <button key={g.type} type="button" className="jc-toc-row" onClick={() => jump(g.type)}
                  style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '6px 8px', borderRadius: 6, cursor: 'pointer', fontSize: 13, color: COLORS.text, fontFamily: 'inherit' }}>
                  {label}
                </button>
              );
            }
            return (
              <div key={g.type} className="jc-toc-row"
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', borderRadius: 6, fontSize: 13, color: COLORS.text }}>
                <input
                  type="checkbox"
                  id={`cat-pick-${g.type}`}
                  checked={on}
                  onChange={() => onToggleType(g.type)}
                  style={{ width: 15, height: 15, cursor: 'pointer', flexShrink: 0, margin: 0 }}
                />
                {/* The label ticks the box (that is what a <label> is for);
                    the count jumps to the section, for the category sort. */}
                <label htmlFor={`cat-pick-${g.type}`}
                  style={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
                  {isCategorySort && present.has(g.type) ? (
                    <>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: on ? 700 : 400 }}>{formatCategoryLabel(g.type)}</span>
                      <span
                        role="button"
                        tabIndex={0}
                        title={t('Jump to this section')}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); jump(g.type); }}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); jump(g.type); } }}
                        style={{ color: COLORS.muted, fontSize: 12, fontWeight: 600, flexShrink: 0, cursor: 'pointer' }}
                      >
                        {g.count}
                      </span>
                    </>
                  ) : label}
                </label>
              </div>
            );
          })}
        </nav>
      </div>
    );
  }

  return (
    <div className="jc-cat-toc-dropdown" style={{ marginBottom: 14 }}>
      <select
        aria-label={t('Jump to a category on this page')}
        value=""
        onChange={(e) => jump(e.target.value)}
        style={{
          width: '100%', maxWidth: 340, padding: '9px 12px', fontSize: 14,
          borderRadius: 8, border: `1px solid ${COLORS.border}`, background: 'white',
          color: COLORS.text, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
        }}
      >
        <option value="">{t('↧ Jump to a category…')}</option>
        {groups.map((g) => (
          <option key={g.type} value={g.type}>{formatCategoryLabel(g.type)} ({g.count})</option>
        ))}
      </select>
    </div>
  );
}
